/* Wade Greens - small storefront behaviours. Loaded once from layout/theme.liquid
   (via snippets/wg-schema-global.liquid). Styling lives in assets/wg-ui.css.

   Three widgets, each a no-op when its markup isn't on the page:
     1. the sliding category strip in the product-block sections
     2. the Growing Zone tab on the PDP and quick view
     3. the Shipping & Returns tab on the PDP and quick view

   The hardiness map itself is painted by assets/wg-zone.js. */
(function () {
  'use strict';

  function initStrip(strip) {
    if (!strip || strip.dataset.wgSlide === '1') return;
    strip.dataset.wgSlide = '1';

    function max() { return strip.scrollWidth - strip.clientWidth; }

    function sync() {
      var m = max();
      if (m <= 2) {
        strip.classList.remove('is-scrollable');
        strip.removeAttribute('data-of');
        return;
      }
      strip.classList.add('is-scrollable');
      var start = strip.scrollLeft > 2;
      var end = strip.scrollLeft < m - 2;
      strip.setAttribute('data-of', start && end ? 'both' : (start ? 'start' : 'end'));
    }

    function reveal(btn) {
      if (!btn || max() <= 2) return;
      var s = strip.getBoundingClientRect(), b = btn.getBoundingClientRect();
      if (b.left < s.left + 8) strip.scrollLeft -= (s.left + 8 - b.left);
      else if (b.right > s.right - 8) strip.scrollLeft += (b.right - (s.right - 8));
    }

    strip.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    if (window.ResizeObserver) {
      try { new ResizeObserver(sync).observe(strip); } catch (e) {}
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(sync).catch(function () {});
    }

    strip.addEventListener('wheel', function (e) {
      var m = max();
      if (m <= 2 || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (e.deltaY < 0 && strip.scrollLeft <= 0) return;
      if (e.deltaY > 0 && strip.scrollLeft >= m - 1) return;
      e.preventDefault();
      strip.scrollLeft += e.deltaY;
    }, { passive: false });

    var down = false, dragging = false, moved = false, startX = 0, startLeft = 0, pid = null;
    var THRESHOLD = 8;

    strip.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || max() <= 2) return;
      down = true; dragging = false; moved = false;
      startX = e.clientX; startLeft = strip.scrollLeft; pid = e.pointerId;
    });
    strip.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) <= THRESHOLD) return;
        dragging = true; moved = true;
        strip.classList.add('is-dragging');
        try { strip.setPointerCapture(pid); } catch (err) {}
      }
      strip.scrollLeft = startLeft - dx;
      e.preventDefault();
    });
    function endDrag() {
      if (!down) return;
      down = false;
      if (dragging) {
        strip.classList.remove('is-dragging');
        if (pid !== null) { try { strip.releasePointerCapture(pid); } catch (err) {} }
      }
      dragging = false; pid = null;
      setTimeout(function () { moved = false; }, 0);
    }
    strip.addEventListener('pointerup', endDrag);
    strip.addEventListener('pointercancel', endDrag);
    strip.addEventListener('dragstart', function (e) { e.preventDefault(); });
    strip.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    Array.prototype.forEach.call(strip.querySelectorAll('[data-epc-tab]'), function (btn) {
      btn.addEventListener('click', function () { reveal(btn); });
      btn.addEventListener('focus', function () { reveal(btn); });
    });

    sync();
    reveal(strip.querySelector('[data-epc-tab].is-active'));
  }

  /* ------------------------------------------------------------------
     GROWING ZONE TAB

     Half-zones are integers: n*2, +1 for the "b" half. 7a is 14, 7b is 15.
     Same encoding the zone pills use, so the two can never disagree.
     ------------------------------------------------------------------ */
  function zoneToCode(str) {
    var m = /^(\d{1,2})\s*([ab])?$/i.exec(String(str || '').trim());
    if (!m) return null;
    var n = parseInt(m[1], 10);
    if (!(n >= 1 && n <= 13)) return null;
    return n * 2 + (String(m[2] || 'a').toLowerCase() === 'b' ? 1 : 0);
  }

  function codeToZone(code) {
    return Math.floor(code / 2) + (code % 2 ? 'b' : 'a');
  }

  function initZone(root) {
    if (!root || root.dataset.wgZoneReady === '1') return;
    root.dataset.wgZoneReady = '1';

    var lo = parseInt(root.dataset.lo, 10);
    var hi = parseInt(root.dataset.hi, 10);
    var label = root.dataset.label || (codeToZone(lo) + '–' + codeToZone(hi));
    if (isNaN(lo) || isNaN(hi)) return;

    var canvas = root.querySelector('[data-wg-zone-canvas]');
    var mapLoaded = false, draw = null;

    function loadMap() {
      if (mapLoaded || !canvas || !root.dataset.grid || !root.dataset.map) return;
      mapLoaded = true;
      Promise.all([
        fetch(root.dataset.grid).then(function (r) { if (!r.ok) throw 0; return r.text(); }),
        fetch(root.dataset.map).then(function (r) { if (!r.ok) throw 0; return r.text(); })
      ]).then(function (res) {
        if (!window.wgZoneMap) throw 0;
        draw = window.wgZoneMap.build(canvas, res[0], res[1], lo, hi);
        if (!draw) throw 0;
        draw(null);
        window.wgZoneMap.ramps(root.querySelector('[data-wg-zone-ramp]'), lo, hi);
      }).catch(function () {
        /* The map is the nice-to-have; the range and the ZIP check are the
           substance, so a map that cannot load removes itself quietly. */
        var wrap = root.querySelector('.wg-zone__mapwrap');
        if (wrap) wrap.hidden = true;
      });
    }

    /* Only fetch once the tab is actually opened. */
    if (canvas && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { loadMap(); io.disconnect(); } });
      }, { rootMargin: '200px' });
      io.observe(canvas);
      var tab = root.closest('.tab-content');
      var link = tab && tab.querySelector('.toggleLink');
      if (link) link.addEventListener('click', loadMap, { once: true });
    } else {
      loadMap();
    }

    var form = root.querySelector('[data-wg-zone-form]');
    var input = root.querySelector('.wg-zone__input');
    var out = root.querySelector('[data-wg-zone-out]');
    if (!form || !input || !out) return;

    function say(state, html) {
      out.setAttribute('data-state', state);
      out.innerHTML = html;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var zip = (input.value || '').trim();
      if (!/^\d{5}$/.test(zip)) { say('warn', 'Enter a 5-digit ZIP code.'); return; }
      say('wait', 'Checking&hellip;');
      loadMap();
      fetch('https://phzmapi.org/' + zip + '.json')
        .then(function (r) {
          if (r.status === 404) throw new Error('notfound');
          if (!r.ok) throw new Error('http');
          return r.json();
        })
        .then(function (d) {
          var code = zoneToCode(d.zone);
          if (code === null) throw new Error('parse');
          var c = d.coordinates || {};
          if (draw && isFinite(+c.lon) && isFinite(+c.lat)) draw([+c.lon, +c.lat]);
          var z = '<strong>' + zip + '</strong> is zone <strong class="wg-z">' + codeToZone(code) + '</strong>';
          if (code >= lo && code <= hi) {
            say('yes', z + ' — this thrives there. Plant it in the ground and leave it out year-round.');
          } else if (code < lo) {
            say('no', z + ', colder than its range of ' + label +
              '. Grow it in a container and bring it somewhere sheltered before the first hard freeze.');
          } else {
            say('no', z + ', warmer than its range of ' + label +
              '. It may not get the winter chill it needs, and summer heat can be hard on it.');
          }
        })
        .catch(function (err) {
          if (err && err.message === 'notfound') {
            say('warn', "We couldn't find that ZIP code. Check the digits and try again.");
          } else {
            say('warn', "The zone lookup isn't answering right now. This plant is hardy in zones " +
              label + ' — the map below shows where that lands.');
          }
        });
    });
  }

  /* ------------------------------------------------------------------
     SHIPPING & RETURNS TAB

     The snippet renders one panel per variant and hides all but the selected
     one, so switching a variant never needs a round trip. The product form's
     hidden [name="id"] is the one thing every Halo layout agrees on.
     ------------------------------------------------------------------ */
  function initShip(root) {
    if (!root || root.dataset.wgShipReady === '1') return;
    root.dataset.wgShipReady = '1';

    var panels = root.querySelectorAll('[data-wg-ship-panel]');
    if (!panels.length) return;

    function show(variantId) {
      var shown = null;
      Array.prototype.forEach.call(panels, function (p) {
        var match = String(p.dataset.variant) === String(variantId);
        p.hidden = !match;
        if (match) shown = p;
      });
      if (!shown) {
        panels[0].hidden = false;
        shown = panels[0];
      }
      var cls = shown.dataset.class || 'live_plant_ground';
      root.setAttribute('data-class', cls);
      Array.prototype.forEach.call(root.querySelectorAll('[data-wg-ship-when]'), function (b) {
        b.hidden = b.dataset.wgShipWhen.split(',').indexOf(cls) === -1;
      });
    }

    var scope = root.closest('.productView, .product-single, .halo-quick-view, body') || document;
    var field = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    if (!field) {
      show(panels[0].dataset.variant);
      return;
    }

    show(field.value);
    var form = field.closest('form');
    if (form) form.addEventListener('change', function () { show(field.value); });
    if (window.MutationObserver) {
      new MutationObserver(function () { show(field.value); })
        .observe(field, { attributes: true, attributeFilter: ['value'] });
    }
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('.epc__tabs'), initStrip);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-wg-zone]'), initZone);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-wg-ship]'), initShip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
  });

  /* Quick view builds its markup after load, so watch for new widgets. */
  if (window.MutationObserver) {
    var pending = null;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && (n.matches('[data-wg-zone],[data-wg-ship],.epc__tabs') ||
              n.querySelector('[data-wg-zone],[data-wg-ship],.epc__tabs'))) {
            clearTimeout(pending);
            pending = setTimeout(function () { scan(document); }, 60);
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
